import React, { useState } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  InputBase,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch } from "react-redux";
import { fmtInt, fmtMoney } from "../../../utils/format";
import {
  useUpdateDirectBuyMetricsMutation,
  WIPCampaignMetrics,
} from "../../../store/apis/wipApi";
import { setToastMessage } from "../../../store/slices/app/appSlice";
import { ManualMetricField } from "./columns";
import { getErrorMessage, parseManualDraft } from "./derive";

const formatValue = (field: ManualMetricField, value: number | null) =>
  value == null
    ? "—"
    : field === "platformInversion"
      ? fmtMoney(value, 2)
      : fmtInt(value);

interface ManualMetricEditCellProps {
  field: ManualMetricField;
  /** Saved manual value; null = not filled in yet. */
  value: number | null;
  /** Draft in progress; undefined = not editing. */
  draft: string | undefined;
  saving: boolean;
  onStart: () => void;
  onDraftChange: (value: string) => void;
  onCancel: () => void;
  onSave: (value: number | null) => void;
}

/**
 * Editable cell of a direct-buy row: the real metric the partner reports,
 * which on a connected line would arrive through the API. Click → input;
 * Enter saves, Esc cancels, empty clears. Clicks inside do not reach the
 * table's cell selection.
 */
const ManualMetricEditCell: React.FC<ManualMetricEditCellProps> = ({
  field,
  value,
  draft,
  saving,
  onStart,
  onDraftChange,
  onCancel,
  onSave,
}) => {
  const [hover, setHover] = useState(false);

  if (draft === undefined) {
    return (
      <Tooltip
        title={
          field === "platformInversion"
            ? "Inversión de plataforma real (neta) que da el partner. El report le aplica el margen del brief"
            : "Dato real que da el partner (total acumulado)"
        }
        enterDelay={350}
      >
        <Box
          component="span"
          onClick={(e) => {
            e.stopPropagation();
            onStart();
          }}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            cursor: "text",
          }}
        >
          <EditIcon sx={{ fontSize: 12, opacity: hover ? 1 : 0.55 }} />
          {formatValue(field, value)}
        </Box>
      </Tooltip>
    );
  }

  const parsed = parseManualDraft(field, draft);
  const invalid = parsed === undefined;
  const canSave = !invalid && parsed !== value && !saving;

  return (
    <Box
      onClick={(e) => e.stopPropagation()}
      sx={{ display: "flex", alignItems: "center", gap: 0.25 }}
    >
      <InputBase
        autoFocus
        value={draft}
        disabled={saving}
        onChange={(e) => onDraftChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && canSave) onSave(parsed as number | null);
          if (e.key === "Escape") onCancel();
        }}
        inputProps={{
          inputMode: field === "platformInversion" ? "decimal" : "numeric",
          "aria-label": field,
        }}
        error={invalid}
        sx={{
          flex: 1,
          minWidth: 0,
          fontSize: 12,
          "& input": { textAlign: "right", py: 0, px: 0.5 },
          border: 1,
          borderColor: invalid ? "error.main" : "primary.main",
          borderRadius: 0.5,
        }}
      />
      {saving ? (
        <CircularProgress size={14} sx={{ mx: 0.5 }} />
      ) : (
        <Tooltip title="Guardar">
          <span>
            <IconButton
              size="small"
              color="primary"
              disabled={!canSave}
              onClick={() => onSave(parsed as number | null)}
              sx={{ p: 0.25 }}
            >
              <CheckIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </span>
        </Tooltip>
      )}
      <Tooltip title="Cancelar">
        <IconButton
          size="small"
          onClick={onCancel}
          disabled={saving}
          sx={{ p: 0.25 }}
        >
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

/**
 * Cell of a manual metric on a direct-buy row: keeps its own draft and saves
 * through the API, so both WIP tables can drop it in place of the plain value.
 */
export const DirectBuyMetricCell: React.FC<{
  row: Pick<WIPCampaignMetrics, "associatedCampaignId" | "manualMetrics">;
  field: ManualMetricField;
}> = ({ row, field }) => {
  const dispatch = useDispatch();
  const [draft, setDraft] = useState<string | undefined>(undefined);
  const [save, { isLoading }] = useUpdateDirectBuyMetricsMutation();
  const value = row.manualMetrics?.[field] ?? null;

  const onSave = async (next: number | null) => {
    try {
      await save({
        associatedCampaignId: row.associatedCampaignId,
        body: { [field]: next },
      }).unwrap();
      setDraft(undefined);
    } catch (err) {
      dispatch(
        setToastMessage({
          message: getErrorMessage(err) || "No se ha podido guardar el dato",
          severity: "error",
        })
      );
    }
  };

  return (
    <ManualMetricEditCell
      field={field}
      value={value}
      draft={draft}
      saving={isLoading}
      onStart={() => setDraft(value == null ? "" : String(value))}
      onDraftChange={setDraft}
      onCancel={() => setDraft(undefined)}
      onSave={onSave}
    />
  );
};

export default ManualMetricEditCell;
